import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Session is carried by an httpOnly cookie; ask the backend who we are.
    // Run once on mount only — api & state setters are stable references.
    api.get("/auth/me")
      .then(({ data }) => setUser(data || false))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const studentLogin = useCallback(async (first_name, last_name, pin) => {
    const { data } = await api.post("/auth/student-login", { first_name, last_name, pin });
    if (data.first_login) sessionStorage.setItem("mz_first_login", "1");
    setUser(data.user);
    return data.user;
  }, []);

  const adminLogin = useCallback(async (code) => {
    const { data } = await api.post("/auth/admin-login", { code });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Logout request failed, clearing session locally", err);
      }
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, ready, studentLogin, adminLogin, logout, setUser }),
    [user, ready, studentLogin, adminLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
